import { CalendarClock, CheckCircle2, LoaderCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { loadSavedBookingState } from "@/lib/storage";
import { wasteKingAssets } from "@/lib/siteAssets";

type OrderStatusPageProps = {
  bookingReference: string;
};

function getScheduledDate() {
  const scheduledDate = new Date();
  scheduledDate.setDate(scheduledDate.getDate() + 3);

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(scheduledDate);
}

export function OrderStatusPage({ bookingReference }: OrderStatusPageProps) {
  const bookingState = loadSavedBookingState();
  const savedReference = bookingState?.bookingReference || bookingReference;
  const scheduledDate = getScheduledDate();

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen w-full max-w-4xl flex-col px-4 py-5 sm:px-6 lg:px-8">
        <header className="mb-6 flex items-center justify-between rounded-lg bg-accent px-5 py-4 shadow-[0_18px_45px_hsl(229_61%_34%_/_0.18)]">
          <img src={wasteKingAssets.logos.blue} alt="Waste King" className="h-auto w-40 max-w-full" />
          <Badge className="shrink-0">Order overview</Badge>
        </header>

        <Card className="overflow-hidden rounded-lg">
          <CardHeader className="border-b-4 border-primary bg-accent text-accent-foreground">
            <CardTitle>Booking status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 pt-6 sm:pt-7">
            <div>
              <p className="text-sm font-bold text-muted-foreground">Booking reference</p>
              <h1 className="mt-1 break-words text-3xl font-black tracking-normal text-foreground sm:text-4xl">
                {savedReference}
              </h1>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <section className="rounded-lg border-2 border-accent/10 bg-white p-4">
                <CheckCircle2 className="h-6 w-6 text-primary" aria-hidden="true" />
                <h2 className="mt-4 text-lg font-black tracking-normal">Booking succeeded</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">Your payment was completed and the booking has been created.</p>
              </section>

              <section className="rounded-lg border-2 border-accent/10 bg-white p-4">
                <LoaderCircle className="h-6 w-6 text-accent" aria-hidden="true" />
                <h2 className="mt-4 text-lg font-black tracking-normal">In progress</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">Waste King is preparing the order for fulfilment.</p>
              </section>

              <section className="rounded-lg border-2 border-accent/10 bg-white p-4">
                <CalendarClock className="h-6 w-6 text-accent" aria-hidden="true" />
                <h2 className="mt-4 text-lg font-black tracking-normal">In three days' time</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{scheduledDate}</p>
              </section>
            </div>

            <div className="rounded-lg border-2 border-accent/10 bg-secondary p-4">
              <p className="text-sm font-extrabold uppercase text-accent">Current status</p>
              <p className="mt-2 text-2xl font-black tracking-normal text-foreground">Succeeded and in progress</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
